import { defineAsyncComponent, type Component } from 'vue';
import { useI18n } from 'vue-i18n';
import { isNil } from 'lodash-es';
import type { Airflow } from '@aviata/platform-types';

const FARE_FEATURE_TYPE = {
  RECOMMENDED: 'recommended',
  DISCOUNT: 'discount',
} as const;

type FareFeatureType =
  (typeof FARE_FEATURE_TYPE)[keyof typeof FARE_FEATURE_TYPE];

export interface FareFeature {
  type: FareFeatureType;
  text: string;
  icon: Component;
}

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: NonNullable<T[P]> };

interface FareFeatureDefinition<
  T extends Airflow.FareFamily.Choice = Airflow.FareFamily.Choice,
> {
  type: FareFeatureType;
  predicate(fare: Airflow.FareFamily.Choice): fare is T;
  text: (fare: T) => string;
  iconLoader: () => Promise<Component>;
}

const recommended: FareFeatureDefinition<
  WithRequired<Airflow.FareFamily.Choice, 'promotions'>
> = {
  type: FARE_FEATURE_TYPE.RECOMMENDED,
  predicate: (
    fare,
  ): fare is WithRequired<Airflow.FareFamily.Choice, 'promotions'> =>
    !!fare.promotions?.some(p => p.recommended),
  text: () => useI18n().t('search.labels.recommended'),
  iconLoader: () =>
    import('@/assets/images/fare/recommended-icon.svg?component'),
};

const discount: FareFeatureDefinition<
  WithRequired<Airflow.FareFamily.Choice, 'discount'>
> = {
  type: FARE_FEATURE_TYPE.DISCOUNT,
  predicate: (
    fare,
  ): fare is WithRequired<Airflow.FareFamily.Choice, 'discount'> =>
    !isNil(fare.discount),
  text: fare => fare.discount.title || '',
  iconLoader: () => import('@/assets/images/fare/discount-icon.svg?component'),
};

const definitions = [recommended, discount];

export function createFareFeatures(
  fare: Airflow.FareFamily.Choice,
): FareFeature[] {
  return (definitions as FareFeatureDefinition[]).reduce((features, def) => {
    if (def.predicate(fare)) {
      features.push({
        type: def.type,
        text: def.text(fare),
        icon: defineAsyncComponent(def.iconLoader),
      });
    }
    return features;
  }, [] as FareFeature[]);
}
